import { useModoConsultaEvaluacion } from '../../../../hooks/useModoConsultaEvaluacion';
import React, { useState, useContext } from 'react';
import { AppContext } from '../../../../Context';
import { collection, addDoc } from "firebase/firestore";
import { db } from '../../../../firebase/firebase';
import { Form, Button, Container, Card, Table, Row, Col, Badge } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { 
    FaLightbulb, FaCogs, FaPaintBrush, 
    FaFire, FaWater, FaUtensils, 
    FaDice, FaHandPaper, FaSmoking, 
    FaWalking, FaGlasses, FaSearch, FaLemon 
} from 'react-icons/fa';

// Componente para la teoría (3.md) y práctica (4.md) combinadas
const M11_Ejercicio9 = ({ cerrarModal }) => {
    const { currentUser } = useContext(AppContext);
    const modoConsulta = useModoConsultaEvaluacion();
    const [step, setStep] = useState(1);
    
    // Lista completa de palabras del 4.md
    const palabras = [
        "Pared", "Esquina", "Amar", "Coche", "Tapa", "Lata", "Moto",
        "Pluma", "Taco", "Moño", "Átomo", "Meta", "Dedo", "Taza",
        "Pecera", "Recámara", "Lámpara", "Cuadro", "Pulsera", "Hijo", "Amigo"
    ];

    const [respuestas, setRespuestas] = useState(
        palabras.reduce((acc, palabra) => {
            acc[palabra] = { numero: '', asociacion: '' };
            return acc;
        }, {})
    );

    const handleChange = (palabra, campo, valor) => {
        setRespuestas(prev => ({
            ...prev,
            [palabra]: {
                ...prev[palabra],
                [campo]: valor
            }
        }));
    };

    const nextStep = () => setStep(prev => prev + 1);
    const prevStep = () => setStep(prev => prev - 1);

    const calcularPuntaje = () => {
        let puntaje = 0;
        let respondidas = 0;

        palabras.forEach(palabra => {
            const r = respuestas[palabra];
            if (r.numero.trim() || r.asociacion.trim()) {
                respondidas++;
                // Puntaje básico: 1 pt por intentar, +1 si tiene número, +1 si tiene asociación, +2 si es coherente (simulado por longitud)
                if (r.numero.trim()) puntaje += 1;
                if (r.asociacion.trim().length > 5) puntaje += 2;
            }
        });

        // Escalar a 20 puntos máximo (aprox)
        const puntajeFinal = Math.min(20, Math.round((puntaje / (palabras.length * 3)) * 20));
        return { puntajeFinal, respondidas };
    };

    const enviarResultados = async () => {
        if (modoConsulta) { cerrarModal?.(); return; }

        const { puntajeFinal, respondidas } = calcularPuntaje();
        
        if (respondidas < 5) {
            Swal.fire({
                icon: 'warning',
                title: 'Pocas respuestas',
                text: 'Por favor, completa al menos 5 palabras con sus números y asociaciones antes de enviar.',
                confirmButtonColor: '#3085d6'
            });
            return;
        }

        try {
            await addDoc(collection(db, "evaluaciones"), {
                userId: currentUser.uid,
                nombre: "Ejercicio 9 - Mnemotecnia (Teoría y Práctica)",
                nivel: "Avanzado",
                modulo: "11",
                createdAt: new Date(),
                tipo: "Mnemotecnia",
                respuestas: respuestas,
                puntaje: puntajeFinal,
                puntajeMaximo: 20
            });
            Swal.fire({
                title: '¡Evaluación Enviada!',
                text: `Resultados enviados con éxito. Puntaje estimado: ${puntajeFinal}/20.`,
                icon: 'success',
                confirmButtonColor: '#3085d6',
                confirmButtonText: 'Excelente'
            }).then((result) => {
                if (result.isConfirmed) {
                    cerrarModal();
                }
            });
        } catch (error) {
            console.error("Error al enviar las respuestas: ", error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Error al enviar las respuestas.',
                confirmButtonColor: '#3085d6'
            });
        }
    };

    return (
        <Container>
            <h3 className="text-center mb-4">Ejercicio 9 - Sistema de Mnemotecnia</h3>
            
            {step === 1 && (
                <div className="teoria-section">
                    <Card className="mb-4 shadow-sm border-left-info">
                        <Card.Body>
                            <Card.Title>1. Introducción a la Mnemotecnia</Card.Title>
                            <Card.Text>
                                La mnemotecnia es una técnica que permite mejorar la memoria mediante la asociación de ideas. 
                                En lugar de memorizar por repetición, usamos conexiones mentales fuertes.
                            </Card.Text>
                            
                            <h5 className="mt-4 mb-3">Tipos de Asociación:</h5>
                            <Row className="g-3">
                                <Col md={4}>
                                    <Card className="h-100 text-center p-3 bg-light">
                                        <FaLightbulb size={30} className="text-warning mx-auto mb-2" />
                                        <h6>Lógicas</h6>
                                        <small>Relaciones causales o evidentes</small>
                                    </Card>
                                </Col>
                                <Col md={4}>
                                    <Card className="h-100 text-center p-3 bg-light">
                                        <FaCogs size={30} className="text-secondary mx-auto mb-2" />
                                        <h6>Artificiales</h6>
                                        <small>Códigos y reglas predefinidas</small>
                                    </Card>
                                </Col>
                                <Col md={4}>
                                    <Card className="h-100 text-center p-3 bg-light">
                                        <FaPaintBrush size={30} className="text-primary mx-auto mb-2" />
                                        <h6>Ingeniosas</h6>
                                        <small>Creatividad y asociación libre</small>
                                    </Card>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    <div className="d-flex justify-content-end mt-4">
                        <Button variant="primary" size="lg" onClick={nextStep}>Siguiente: Sistema Simbólico</Button>
                    </div>
                </div>
            )}

            {step === 2 && (
                <div className="sistema-section">
                    <Card className="mb-4 shadow-sm border-left-primary">
                        <Card.Body>
                            <Card.Title>2. El Sistema Simbólico</Card.Title>
                            <Card.Text>
                                Este sistema asocia cada número del 0 al 9 con un objeto específico por su forma o concepto. 
                                ¡Memoriza esta tabla, será tu herramienta principal!
                            </Card.Text>

                            <Row className="g-3 mt-2 text-center">
                                {[
                                    { n: 1, obj: "Cirio", icon: <FaFire size={24}/> },
                                    { n: 2, obj: "Cisne", icon: <FaWater size={24}/> },
                                    { n: 3, obj: "Tridente", icon: <FaUtensils size={24}/> },
                                    { n: 4, obj: "Dado", icon: <FaDice size={24}/> },
                                    { n: 5, obj: "Mano", icon: <FaHandPaper size={24}/> },
                                    { n: 6, obj: "Pipa", icon: <FaSmoking size={24}/> },
                                    { n: 7, obj: "Bastón", icon: <FaWalking size={24}/> },
                                    { n: 8, obj: "Gafas", icon: <FaGlasses size={24}/> },
                                    { n: 9, obj: "Lupa", icon: <FaSearch size={24}/> },
                                    { n: 0, obj: "Naranja", icon: <FaLemon size={24}/> }
                                ].map((item) => (
                                    <Col xs={6} md={3} lg={2} key={item.n}>
                                        <Card className="h-100 p-2 border-primary">
                                            <Badge bg="primary" className="mb-2" style={{ fontSize: '1.2em' }}>{item.n}</Badge>
                                            <div className="text-primary mb-2">{item.icon}</div>
                                            <strong>{item.obj}</strong>
                                        </Card>
                                    </Col>
                                ))}
                            </Row>

                            <div className="mt-4 p-3 bg-light rounded">
                                <h6>Ejemplos de aplicación:</h6>
                                <p className="mb-1"><strong>Bote</strong> = 61 (Pipa + Cirio)</p>
                                <p className="mb-0"><strong>Mata</strong> = 31 (Tridente + Cirio)</p>
                            </div>
                        </Card.Body>
                    </Card>

                    <div className="d-flex justify-content-between mt-4">
                        <Button variant="secondary" size="lg" onClick={prevStep}>Anterior</Button>
                        <Button variant="primary" size="lg" onClick={nextStep}>Siguiente: Práctica</Button>
                    </div>
                </div>
            )}

            {step === 3 && (
                <div className="practica-section">
                    <Card className="mb-4 shadow-sm border-left-success">
                        <Card.Body>
                            <Card.Title>3. Aplicación Práctica</Card.Title>
                            <Card.Text>
                                Convierte las siguientes palabras en números usando el sistema simbólico que acabas de aprender, 
                                y describe una imagen mental o asociación creativa que te ayude a recordarlo.
                                <br/><br/>
                                <em>Ejemplo: "Pared" → Número: 5 (Mano) → Asociación: Imagino una mano gigante pintando una pared.</em>
                            </Card.Text>
                        </Card.Body>
                    </Card>

                    <Form>
                        <div className="table-responsive" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                            <Table striped bordered hover stickyHeader>
                                <thead className="table-dark" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                                    <tr>
                                        <th style={{ width: '20%' }}>Palabra</th>
                                        <th style={{ width: '20%' }}>Número Asociado</th>
                                        <th style={{ width: '60%' }}>Asociación / Refuerzo Visual</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {palabras.map((palabra, index) => (
                                        <tr key={index}>
                                            <td className="align-middle fw-bold text-primary">{palabra}</td>
                                            <td>
                                                <Form.Control 
                                                    type="text" 
                                                    placeholder="Ej. 12"
                                                    value={respuestas[palabra].numero}
                                                    onChange={(e) => handleChange(palabra, 'numero', e.target.value)}
                                                />
                                            </td>
                                            <td>
                                                <Form.Control 
                                                    as="textarea"
                                                    rows={2}
                                                    placeholder="Describe tu imagen mental..."
                                                    value={respuestas[palabra].asociacion}
                                                    onChange={(e) => handleChange(palabra, 'asociacion', e.target.value)}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>

                        <div className="d-flex justify-content-between mt-4">
                            <Button variant="secondary" size="lg" onClick={prevStep}>Volver a la Teoría</Button>
                            <Button variant="success" size="lg" onClick={enviarResultados}>
                                Enviar Resultados Finales
                            </Button>
                        </div>
                    </Form>
                </div>
            )}
        </Container>
    );
};

export default M11_Ejercicio9;